import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { useBillingPolling } from './useBillingPolling';

const TIER_DISPLAY_NAMES = {
  pro: { full: 'Apex AI', short: 'Apex AI' },
  premium: { full: '1:1 with Coach Lewis', short: '1:1 Coaching' },
};

function getTierNames(tier) {
  return TIER_DISPLAY_NAMES[tier] || { full: 'Apex', short: 'Apex AI' };
}

function CheckingState() {
  return (
    <>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0F4F4] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#004C54] animate-spin" />
      </div>
      <h1 className="font-heading text-xl font-bold mb-2">Confirming your payment...</h1>
      <p className="text-sm text-foreground/65">This usually takes a few seconds.</p>
    </>
  );
}

function SuccessState({ tier, onContinue }) {
  const names = getTierNames(tier);
  return (
    <>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#004C54]/10 flex items-center justify-center">
        <CheckCircle2 className="h-6 w-6 text-[#004C54]" />
      </div>
      <h1 className="font-heading text-2xl font-bold mb-2">You're in.</h1>
      <p className="text-sm text-foreground/65 mb-5">
        Welcome to <strong className="text-foreground">{names.full}</strong>. Your 7-day trial has started.
      </p>
      <Button
        onClick={onContinue}
        className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-6"
        data-testid="go-to-dashboard"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        Start using {names.short}
      </Button>
    </>
  );
}

function TimeoutState({ onContinue }) {
  return (
    <>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0F4F4] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#004C54]" />
      </div>
      <h1 className="font-heading text-xl font-bold mb-2">Still confirming...</h1>
      <p className="text-sm text-foreground/65 mb-5">
        Stripe is taking longer than usual. Your subscription will activate as soon as the payment clears.
      </p>
      <Button onClick={onContinue} variant="outline" className="h-11" data-testid="go-dashboard-anyway">
        Go to dashboard
      </Button>
    </>
  );
}

function FailedState({ onRetry, onContinue }) {
  return (
    <>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
        <XCircle className="h-6 w-6 text-destructive" />
      </div>
      <h1 className="font-heading text-xl font-bold mb-2">Payment didn't go through</h1>
      <p className="text-sm text-foreground/65 mb-5">
        No charge was made. You can try again or go back to your dashboard.
      </p>
      <div className="flex gap-2 justify-center">
        <Button onClick={onRetry} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="retry-checkout">
          Try again
        </Button>
        <Button onClick={onContinue} variant="outline" data-testid="back-dashboard">
          Dashboard
        </Button>
      </div>
    </>
  );
}

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = searchParams.get('session_id');

  const { state, tier } = useBillingPolling(sessionId, refresh);

  const goToDashboard = () => navigate('/app/dashboard');
  const goToPricing = () => navigate('/app/pricing');

  return (
    <div className="max-w-[480px] mx-auto py-10" data-testid="billing-success-page">
      <Card className="border-2 border-[#004C54]/20">
        <CardContent className="p-7 sm:p-8 text-center">
          {state === 'checking' && <CheckingState />}
          {state === 'success' && <SuccessState tier={tier} onContinue={goToDashboard} />}
          {state === 'timeout' && <TimeoutState onContinue={goToDashboard} />}
          {state === 'failed' && <FailedState onRetry={goToPricing} onContinue={goToDashboard} />}
        </CardContent>
      </Card>
    </div>
  );
}
